import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MDA_OPTIONS, mdaName } from '../../lib/constants';
import type { Dataset, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, PageHeader, Spinner, StatusBadge } from '../../ui/components';

export function Datasets() {
  const [ownerMda, setOwnerMda] = useState('');

  const query = useMemo(() => {
    const p = new URLSearchParams({ limit: '100' });
    if (ownerMda) p.set('ownerMda', ownerMda);
    return `/api/v1/opengov/datasets?${p}`;
  }, [ownerMda]);
  const q = useApiQuery<Page<Dataset>>(query);

  return (
    <div>
      <PageHeader
        title="Open Data Catalogue"
        subtitle="Every dataset published by a ministry, department or agency, with live JSON/CSV downloads where enabled."
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="ownerMda">
          Publishing MDA
        </label>
        <select
          id="ownerMda"
          className="field-input max-w-xs"
          value={ownerMda}
          onChange={(e) => setOwnerMda(e.target.value)}
        >
          <option value="">All ministries &amp; departments</option>
          {MDA_OPTIONS.map((m) => (
            <option key={m.code} value={m.code}>
              {m.name}
            </option>
          ))}
        </select>
      </div>

      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? (
        <Spinner />
      ) : (q.data?.items.length ?? 0) === 0 ? (
        <EmptyState title="No datasets found" hint="Try a different ministry filter, or check back later." />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {q.data!.items.map((d) => (
            <Link
              key={d.id}
              to={`/datasets/${encodeURIComponent(d.code)}`}
              className="card flex flex-col gap-2 p-5 transition hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-md"
            >
              <div className="flex items-start justify-between gap-2">
                <span className="font-mono text-[11px] font-semibold text-slate-400">{d.code}</span>
                {d.apiEnabled ? (
                  <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 ring-1 ring-emerald-200">
                    API live
                  </span>
                ) : (
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500 ring-1 ring-slate-200">
                    No live export
                  </span>
                )}
              </div>
              <h3 className="text-base font-bold leading-snug text-slate-900">{d.title}</h3>
              {d.description ? <p className="line-clamp-2 text-sm text-slate-500">{d.description}</p> : null}
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                <span>{mdaName(d.ownerMda)}</span>
                <span aria-hidden>·</span>
                <StatusBadge status={d.updateFrequency} />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
